import Offcanvas from 'bootstrap/js/dist/offcanvas.js'

const CONTENT_SELECTOR = '[data-document-content]'
const TOC_SELECTOR = '[data-document-toc]'
const TOC_LIST_SELECTOR = '[data-document-toc-list]'
const TOC_LINK_SELECTOR = '[data-document-toc-link]'
const HEADING_SELECTOR = 'h2, h3'
const EXTRA_SECTION_SELECTOR = '[data-document-toc-section]'

export default class Navigation {
  constructor(element) {
    this.element = element
    this.contentElement = null
    this.sections = []
    this.sectionObserver = null
    this.navbarResizeObserver = null

    this.handleTocClick = this.handleTocClick.bind(this)
    this.handleHashChange = this.handleHashChange.bind(this)
    this.updateStickyOffset = this.updateStickyOffset.bind(this)
  }

  init() {
    if (!(this.element instanceof HTMLElement)) {
      return
    }

    this.contentElement = this.element.querySelector(CONTENT_SELECTOR)

    if (!(this.contentElement instanceof HTMLElement)) {
      return
    }

    this.enhanceTables()
    this.enhanceImages()
    this.enhanceVideoEmbeds()
    this.enhanceLinks()

    this.sections = this.collectSections()

    this.updateStickyOffset()
    this.initNavbarResizeObserver()

    /*
     * Bij nul of één sectie heeft een inhoudsopgave weinig waarde.
     * De content-enhancements hierboven blijven wel actief.
     */
    if (this.sections.length < 2) {
      this.handleInitialHash()

      return
    }

    this.renderTableOfContents()
    this.revealTableOfContents()
    this.bindEvents()
    this.initSectionObserver()
    this.handleInitialHash()
  }

  collectSections() {
    const headings = Array.from(this.contentElement.querySelectorAll(HEADING_SELECTOR)).filter((heading) => !heading.closest('[data-document-toc-ignore]'))

    const extraSections = Array.from(this.element.querySelectorAll(EXTRA_SECTION_SELECTOR))

    /*
     * Houd alle reeds bestaande IDs bij. Zo overschrijven we nooit
     * bestaande anchors en maken we ook bij dubbele headingtitels
     * altijd unieke IDs.
     */
    const usedIds = new Map()

    document.querySelectorAll('[id]').forEach((element) => {
      if (!element.id) {
        return
      }

      usedIds.set(element.id, (usedIds.get(element.id) ?? 0) + 1)
    })

    const sections = []

    /*
     * Headings komen uit de door de editor beheerde document.content.
     * Bestaande unieke IDs blijven behouden. Alleen wanneer een heading
     * geen ID heeft of wanneer het ID dubbel voorkomt genereren we er één.
     */
    headings.forEach((heading) => {
      const title = heading.textContent?.trim()

      if (!title) {
        return
      }

      const level = heading.tagName === 'H3' ? 3 : 2
      let id = heading.id

      if (!id || (usedIds.get(id) ?? 0) > 1) {
        const baseId = this.slugify(title) || 'section'

        id = this.createUniqueId(baseId, usedIds)
        heading.id = id
      }

      sections.push({
        element: heading,
        id,
        title,
        level,
      })
    })

    /*
     * Onderdelen buiten document.content, zoals producten, FAQ,
     * contact en gerelateerde artikelen, kunnen zichzelf expliciet
     * aanmelden voor de inhoudsopgave.
     *
     * Voorbeeld:
     *
     * <section
     *   id="document-faq"
     *   data-document-toc-section
     *   data-document-toc-title="Veelgestelde vragen"
     * >
     */
    extraSections.forEach((section) => {
      const id = section.id
      const title = section.dataset.documentTocTitle?.trim()

      if (!id || !title) {
        return
      }

      sections.push({
        element: section,
        id,
        title,
        level: 2,
      })
    })

    /*
     * Headings en extra secties worden apart verzameld. Sorteer ze
     * daarom opnieuw op hun daadwerkelijke positie in de DOM.
     */
    sections.sort((first, second) => {
      if (first.element === second.element) {
        return 0
      }

      const position = first.element.compareDocumentPosition(second.element)

      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1
      }

      if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1
      }

      return 0
    })

    /*
     * Een vaste index is handig voor data-attributen en latere
     * analytics zonder afhankelijk te zijn van de DOM-positie.
     */
    return sections.map((section, index) => ({
      ...section,
      index,
    }))
  }

  slugify(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  createUniqueId(baseId, usedIds) {
    let id = baseId
    let suffix = 2

    while (usedIds.has(id)) {
      id = `${baseId}-${suffix}`
      suffix += 1
    }

    usedIds.set(id, 1)

    return id
  }

  renderTableOfContents() {
    this.element.querySelectorAll(TOC_LIST_SELECTOR).forEach((container) => {
      container.replaceChildren(this.createTableOfContentsList())
    })
  }

  createTableOfContentsList() {
    const list = document.createElement('ul')

    list.className = 'nav flex-column gap-1'

    let currentPrimaryItem = null
    let currentSecondaryList = null

    this.sections.forEach((section) => {
      const item = this.createTableOfContentsItem(section)

      /*
       * Een H3 wordt onder de laatst gevonden H2 geplaatst.
       * Handmatige secties zoals FAQ en Producten zijn altijd level 2
       * en beginnen dus weer een nieuw hoofdniveau.
       */
      if (section.level === 3 && currentPrimaryItem) {
        if (!currentSecondaryList) {
          currentSecondaryList = document.createElement('ul')
          currentSecondaryList.className = 'nav flex-column gap-1 mt-1 ms-3'

          currentPrimaryItem.append(currentSecondaryList)
        }

        currentSecondaryList.append(item)

        return
      }

      list.append(item)

      if (section.level === 2) {
        currentPrimaryItem = item
        currentSecondaryList = null
      }
    })

    return list
  }

  createTableOfContentsItem(section) {
    const item = document.createElement('li')
    const link = document.createElement('a')

    item.className = 'nav-item'

    link.className = 'nav-link rounded-2'
    link.href = this.createHash(section.id)
    link.textContent = section.title

    link.dataset.documentTocLink = ''
    link.dataset.sectionId = section.id
    link.dataset.sectionIndex = String(section.index)
    link.dataset.headingLevel = String(section.level)

    item.append(link)

    return item
  }

  revealTableOfContents() {
    this.element.querySelectorAll(TOC_SELECTOR).forEach((element) => {
      element.removeAttribute('hidden')
    })
  }

  bindEvents() {
    this.element.addEventListener('click', this.handleTocClick)
    window.addEventListener('hashchange', this.handleHashChange)
  }

  initSectionObserver() {
    if (!('IntersectionObserver' in window)) {
      return
    }

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => {
            return first.boundingClientRect.top - second.boundingClientRect.top
          })

        const activeEntry = visibleEntries[0]

        if (!(activeEntry?.target instanceof HTMLElement)) {
          return
        }

        this.setActiveSection(activeEntry.target.id)
      },
      {
        /*
         * De actieve zone ligt in het bovenste deel van het viewport.
         * Daardoor verandert de TOC rond het moment waarop een nieuwe
         * sectie daadwerkelijk als huidige sectie gelezen wordt.
         */
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0,
      },
    )

    this.sections.forEach((section) => {
      this.sectionObserver.observe(section.element)
    })

    const hashSectionId = this.getSectionIdFromHash()

    if (hashSectionId) {
      this.setActiveSection(hashSectionId)

      return
    }

    if (this.sections[0]) {
      this.setActiveSection(this.sections[0].id)
    }
  }

  setActiveSection(sectionId) {
    if (!sectionId) {
      return
    }

    this.element.querySelectorAll(TOC_LINK_SELECTOR).forEach((link) => {
      const isActive = link.dataset.sectionId === sectionId

      link.classList.toggle('active', isActive)

      if (isActive) {
        link.setAttribute('aria-current', 'location')
      } else {
        link.removeAttribute('aria-current')
      }
    })
  }

  handleTocClick(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    const link = event.target.closest(TOC_LINK_SELECTOR)

    if (!(link instanceof HTMLAnchorElement) || !this.element.contains(link)) {
      return
    }

    const sectionId = link.dataset.sectionId
    const target = sectionId ? document.getElementById(sectionId) : null

    if (!(target instanceof HTMLElement)) {
      return
    }

    event.preventDefault()

    const offcanvasElement = link.closest('[data-document-toc-offcanvas]')

    const navigate = () => {
      this.navigateToSection(sectionId, {
        updateHistory: true,
        behavior: this.getScrollBehavior(),
      })
    }

    /*
     * Op mobiel eerst de inhoudsopgave sluiten.
     * Daarna pas naar de gekozen sectie scrollen.
     */
    if (offcanvasElement instanceof HTMLElement && offcanvasElement.classList.contains('show')) {
      offcanvasElement.addEventListener('hidden.bs.offcanvas', navigate, {
        once: true,
      })

      Offcanvas.getOrCreateInstance(offcanvasElement).hide()

      return
    }

    navigate()
  }

  navigateToSection(sectionId, { updateHistory = false, behavior = 'auto' } = {}) {
    const target = document.getElementById(sectionId)

    if (!(target instanceof HTMLElement)) {
      return
    }

    if (updateHistory) {
      const hash = this.createHash(sectionId)

      if (window.location.hash !== hash) {
        window.history.pushState(null, '', hash)
      }
    }

    target.scrollIntoView({
      behavior,
      block: 'start',
    })

    this.setActiveSection(sectionId)
  }

  handleHashChange() {
    const sectionId = this.getSectionIdFromHash()

    if (!sectionId) {
      return
    }

    requestAnimationFrame(() => {
      this.navigateToSection(sectionId, {
        updateHistory: false,
        behavior: 'auto',
      })
    })
  }

  handleInitialHash() {
    const sectionId = this.getSectionIdFromHash()

    if (!sectionId) {
      return
    }

    /*
     * Browsers kunnen alleen automatisch naar een fragment springen
     * wanneer het ID tijdens de eerste HTML-parse al bestaat.
     * Gegenereerde heading-IDs bestaan pas na init(), dus springen
     * we nogmaals zodra de headings zijn verrijkt.
     */
    requestAnimationFrame(() => {
      this.navigateToSection(sectionId, {
        updateHistory: false,
        behavior: 'auto',
      })
    })
  }

  getSectionIdFromHash() {
    if (!window.location.hash) {
      return null
    }

    try {
      return decodeURIComponent(window.location.hash.substring(1))
    } catch {
      return window.location.hash.substring(1)
    }
  }

  createHash(sectionId) {
    return `#${encodeURIComponent(sectionId)}`
  }

  getScrollBehavior() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'auto'
    }

    return 'smooth'
  }

  updateStickyOffset() {
    const navbar = document.querySelector('[data-sticky-navbar]')
    const navbarHeight = navbar instanceof HTMLElement ? navbar.getBoundingClientRect().height : 0

    /*
     * Eén Bootstrap spacer onder de navbar voorkomt dat een heading
     * strak tegen de sticky header komt te staan.
     */
    const offset = Math.ceil(navbarHeight + 16)

    this.element.style.setProperty('--document-sticky-offset', `${offset}px`)
  }

  initNavbarResizeObserver() {
    const navbar = document.querySelector('[data-sticky-navbar]')

    if (!(navbar instanceof HTMLElement)) {
      return
    }

    if ('ResizeObserver' in window) {
      this.navbarResizeObserver = new ResizeObserver(this.updateStickyOffset)

      this.navbarResizeObserver.observe(navbar)

      return
    }

    window.addEventListener('resize', this.updateStickyOffset)
  }

  enhanceTables() {
    this.contentElement.querySelectorAll('table').forEach((table) => {
      table.classList.add('table')

      if (table.closest('.table-responsive')) {
        return
      }

      const wrapper = document.createElement('div')

      wrapper.className = 'table-responsive my-4'

      table.before(wrapper)
      wrapper.append(table)
    })
  }

  enhanceImages() {
    this.contentElement.querySelectorAll('img').forEach((image, index) => {
      image.classList.add('img-fluid')

      /*
       * De eerste afbeelding kan potentieel onderdeel zijn van de
       * eerste viewport. Daarom maken we die niet automatisch lazy.
       */
      if (index > 0 && !image.hasAttribute('loading')) {
        image.loading = 'lazy'
      }

      if (index > 0 && !image.hasAttribute('decoding')) {
        image.decoding = 'async'
      }
    })
  }

  enhanceVideoEmbeds() {
    this.contentElement.querySelectorAll('iframe').forEach((iframe) => {
      const src = iframe.getAttribute('src') || ''

      if (!this.isVideoEmbed(src)) {
        return
      }

      if (!iframe.hasAttribute('loading')) {
        iframe.loading = 'lazy'
      }

      iframe.classList.add('w-100', 'h-100', 'border-0')

      /*
       * Moderne content die al Cartzilla/Bootstrap ratio-markup
       * gebruikt hoeven we niet nogmaals in te pakken.
       */
      if (iframe.closest('.ratio')) {
        return
      }

      /*
       * Oude Bootstrap-content kan nog .embed-responsive gebruiken.
       * We kunnen die bestaande wrapper zonder inhoud te verplaatsen
       * geschikt maken voor Bootstrap 5.
       */
      const legacyWrapper = iframe.closest('.embed-responsive')

      if (legacyWrapper instanceof HTMLElement) {
        legacyWrapper.classList.add('ratio', 'ratio-16x9')

        return
      }

      const wrapper = document.createElement('div')

      wrapper.className = 'ratio ratio-16x9 my-4'

      const parent = iframe.parentElement

      /*
       * Editors zetten een iframe soms als enige element in een <p>.
       * In dat geval vervangen we de lege paragraaf door de wrapper,
       * zodat er geen block-element in een paragraph terechtkomt.
       */
      if (parent instanceof HTMLParagraphElement && parent.children.length === 1 && !parent.textContent?.trim()) {
        parent.before(wrapper)
        wrapper.append(iframe)
        parent.remove()

        return
      }

      iframe.before(wrapper)
      wrapper.append(iframe)
    })
  }

  isVideoEmbed(src) {
    if (!src) {
      return false
    }

    try {
      const url = new URL(src, window.location.href)
      const hostname = url.hostname.toLowerCase()

      return (
        hostname === 'youtu.be' ||
        hostname.endsWith('.youtube.com') ||
        hostname === 'youtube.com' ||
        hostname.endsWith('.youtube-nocookie.com') ||
        hostname === 'youtube-nocookie.com' ||
        hostname === 'player.vimeo.com'
      )
    } catch {
      return false
    }
  }

  enhanceLinks() {
    const downloadPattern = /\.(pdf|doc|docx|xls|xlsx|csv|zip)(?:$|[?#])/i

    this.contentElement.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href')?.trim()

      if (!href) {
        return
      }

      if (href.startsWith('#')) {
        link.dataset.documentAnchorLink = ''
      }

      if (href.toLowerCase().startsWith('tel:')) {
        link.dataset.phoneLink = ''
      }

      if (link.hasAttribute('download') || downloadPattern.test(href)) {
        link.dataset.fileDownload = ''
      }
    })
  }
}
